import { Close } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Typography, useTheme } from '@mui/material';
import { Filter } from 'iconsax-react';
import { type FC, useState } from 'react';

import { PIN_TYPE_CONFIG, type PinType, type PinVisibility } from './mapPins';

type Props = {
  pinTypes: PinType[];
  pinVisibility: PinVisibility;
  onTogglePinVisibility: (type: PinType) => void;
};

export const MapFilters: FC<Props> = ({ pinTypes, pinVisibility, onTogglePinVisibility }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  // Sits right under Google's fullscreen control; the parent wraps the map in a
  // positioned container.
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '3.75rem',
        right: '0.625rem',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '0.5rem',
      }}
    >
      <IconButton
        aria-label='Map filters'
        sx={{
          width: '2.5rem',
          height: '2.5rem',
          borderRadius: '0.625rem',
          border: `1px solid ${theme.palette.SlabGray.Stroke}`,
          backgroundColor: theme.palette.SlabGray[0],
          '&:hover': {
            backgroundColor: theme.palette.SlabGray[50],
          },
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {isOpen ? (
          <Close sx={{ color: theme.palette.SlabIndigo[500] }} />
        ) : (
          <Filter size={20} variant='TwoTone' color={theme.palette.SlabIndigo[500]} />
        )}
      </IconButton>
      {isOpen && (
        <Box
          sx={{
            backgroundColor: theme.palette.SlabGray[0],
            borderRadius: '0.625rem',
            boxShadow: '0 4px 12px rgba(9, 23, 71, 0.12)',
            padding: '1rem',
            minWidth: '16.5rem',
          }}
        >
          <Box display='flex' alignItems='center' gap='0.5rem' mb='0.75rem'>
            <Filter size={20} variant='TwoTone' color={theme.palette.SlabIndigo[500]} />
            <Typography
              variant='body1'
              sx={{ color: theme.palette.SlabGray[800], fontWeight: 600 }}
            >
              Display Pins:
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column' gap='0.5rem'>
            {pinTypes.map((type) => (
              <Box
                key={type}
                component='label'
                display='flex'
                alignItems='center'
                gap='0.5rem'
                sx={{ cursor: 'pointer' }}
              >
                <Checkbox
                  size='small'
                  color='primary'
                  checked={pinVisibility[type]}
                  onChange={() => onTogglePinVisibility(type)}
                  sx={{ padding: 0 }}
                />
                <Box
                  sx={{
                    width: '1rem',
                    height: '1rem',
                    borderRadius: '50%',
                    border: `3px solid ${PIN_TYPE_CONFIG[type].getColor(theme)}`,
                    backgroundColor: theme.palette.SlabGray[0],
                    flexShrink: 0,
                  }}
                />
                <Typography variant='body2' sx={{ color: theme.palette.SlabGray[800] }}>
                  {PIN_TYPE_CONFIG[type].label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
